import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { auth, onAuthStateChanged, getTenantId } from './FirebaseAuth'
import { getDocsFromAuthentication, getFeatureFlags } from '../firebase/cloudFunction/index'
import { getPermissions } from '../firebase/fireStore/roleManagementDBMethods'
import {
  TENANT_PERMISSION_TYPE,
  ADMIN_PERMISSION_TYPE,
  FIREBASE_USERNAME,
  TENANT_CREATED_TIME,
  LOGIN_TYPE,
  FETCH_USER_PERMISSION_FIREBASE,
} from '../redux/constants/Auth'
import { Spin } from 'antd'
import './Auth.css'
import checkIsFeatureEnabled from './Util'
import { PENDO_TRACKING } from 'constants/FeatureFlagConstant'
import { initializePendo, setPendoFeatureEnabled } from 'utils/pendoInit'
export const AuthContext = React.createContext()
export const SystemFlagContext = React.createContext()
// Default structure for feature flags context
const defaultFeatureFlagsContext = {
  flags: {
    system: [],
    tenant: {},
  },
  loaded: false,
}

export const FeatureFlagsContext = React.createContext(defaultFeatureFlagsContext)
export const useAuthContext = () => React.useContext(AuthContext)
export const useFeatureFlagsContext = () => React.useContext(FeatureFlagsContext)
export const FeaturesContext = React.createContext({});
export const useFeatures = () => React.useContext(FeaturesContext);

const isFeatureEnabledInEnv = (featureName) => {
  if (!featureName) return false
  const envFeatureFlags = process.env.REACT_APP_FEATURE_FLAGS || ''
  const envFeatureList = envFeatureFlags.split(',').map((f) => f.trim())
  return envFeatureList.includes(featureName)
}
const isFeatureEnabled = (featureName, flags) => checkIsFeatureEnabled(featureName, flags) === 1

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [featureFlags, setFeatureFlags] = useState(null)
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false)
  const [pending, setPending] = useState(true)
  const [features, setFeatures] = useState({})

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        setPending(true)
        // console.log('User - ' + JSON.stringify(user))
        await setCustomClaims(user)
        await addFeatureFlags(user)
      } else {
        setPendoFeatureEnabled(false)
      }

      setCurrentUser(user)
      setPending(false)
    })
  }, [])

useEffect(() => {
  if (featureFlagsLoaded && featureFlags && Array.isArray(featureFlags.system)) {
    const updatedFeaturesArray = featureFlags.system.map((featureConfig) => {
      const isEnabled = checkIsFeatureEnabled(featureConfig.featureName, featureFlags);
      console.log(`Feature: ${featureConfig.featureName}, Enabled: ${isEnabled}`); // Debugging log
      return { featureName: featureConfig.featureName, isEnabled };
    });

    const updatedFeatures = updatedFeaturesArray.reduce((acc, { featureName, isEnabled }) => {
      acc[featureName] = isEnabled;
      return acc;
    }, {});

    setFeatures(updatedFeatures);
  }
}, [featureFlagsLoaded, featureFlags]);

  async function setCustomClaims(user) {
    let idTokenResult = await user.getIdTokenResult()

    user.claims = idTokenResult.claims
    user.claims['role'] =
      user.claims.role === TENANT_PERMISSION_TYPE ? ADMIN_PERMISSION_TYPE : TENANT_PERMISSION_TYPE
    if (user.claims && user.claims.tenantId) {
      let tenantID = user.claims.tenantId
      let uuid
      if (user.claims.role === ADMIN_PERMISSION_TYPE) {
        uuid = tenantID
        tenantID = user.claims.identity
      } else {
        uuid = tenantID
      }
      let userPermissions = await getPermissions(tenantID, uuid)
      if (userPermissions?.permissions?.length) {
        user.claims['userPermissions'] = userPermissions
      } else {
        user.claims['userPermissions'] = { permissions: [] }
      }
    }
    let loginType = user.claims.loginType ? user.claims.loginType : '1'
    if (localStorage.getItem(LOGIN_TYPE)) {
      loginType = localStorage.getItem(LOGIN_TYPE)
    }

    console.log('LOGIN TYPE VALUS IS ', loginType)
    if (user.claims.role === ADMIN_PERMISSION_TYPE && loginType == '1') {
      let getPerFir = ''
      let doc
      let getUserPermissionFlag = localStorage.getItem(FETCH_USER_PERMISSION_FIREBASE)
      if (getUserPermissionFlag && getUserPermissionFlag === '1') {
        getPerFir = getUserPermissionFlag
        doc = await getDocsFromAuthentication(user.uid, getPerFir)
      } else if (!getUserPermissionFlag) {
        localStorage.setItem(FETCH_USER_PERMISSION_FIREBASE, '1')
        doc = await getDocsFromAuthentication(user.uid, '')
      }
      // let doc = await getDocsFromAuthentication(user.uid, getPerFir)
      user.claims[FIREBASE_USERNAME] = doc.data.userName
      user.claims['enrollmentTokenAvailable'] = doc.data.enrollmentTokenAvailable
      user.claims['user_id'] = doc.data.adminTenantId
      user.claims[TENANT_CREATED_TIME] = doc.data.createdDateTime
      user.claims['loginType'] = '1'
      user.claims['friendlyId'] = doc.data.friendlyCompanyId
      user.claims['userPermissions'] = { permissions: doc.data.permissions }
      user.claims['customRoleName'] = doc.data.customRoleName
    } else if (user.claims.role === ADMIN_PERMISSION_TYPE && loginType == '2') {
      user.claims['user_id'] = user.claims.identity
    }
  }

  async function addFeatureFlags(user) {
    // Reset the loaded state when starting a new fetch
    setFeatureFlagsLoaded(false)
    setPendoFeatureEnabled(false)
    let isPendoEnabled = false

    if (!user || !user.claims) {
      console.log('addFeatureFlags: No user or tenantId, skipping feature flags fetch.')
      setFeatureFlags(null)
      setPendoFeatureEnabled(false)
      setFeatureFlagsLoaded(true) // Mark as loaded even if null
      return
    }

    try {
      const tenantId = getTenantId(user)
      console.log('addFeatureFlags: Fetching feature flags for tenant:', tenantId)
      const flags = await getFeatureFlags(tenantId)
      console.log('addFeatureFlags: Fetched flags:', flags)

      if (flags && typeof flags.system !== 'undefined' && typeof flags.tenant !== 'undefined') {
        // Valid flags received from API
        setFeatureFlags(flags)
        isPendoEnabled = isFeatureEnabled(PENDO_TRACKING, flags)
        setPendoFeatureEnabled(isPendoEnabled)
        console.log('addFeatureFlags: Feature flags stored in context.')
      } else {
        // Invalid flags format
        console.warn(
          'addFeatureFlags: Fetched flags are not in the expected format, or getFeatureFlags resolved with an error-like structure. Flags:',
          flags
        )
        setFeatureFlags(null)
        setPendoFeatureEnabled(false)
      }
    } catch (error) {
      // Error during fetch
      console.error(
        'addFeatureFlags: Error fetching or storing feature flags.',
        error?.message ? error.message : error,
        error?.errorDetail ? error.errorDetail : ''
      )
      setFeatureFlags(null)
      setPendoFeatureEnabled(false)
    } finally {
      if (isPendoEnabled) {
        await initializePendo(user)
      }
      // Always mark as loaded when the API call completes (success or failure)
      setFeatureFlagsLoaded(true)
      console.log('addFeatureFlags: Feature flags loading completed.')
    }
  }

  if (pending) {
    return (
      <>
        <Spin size="large" tip="Loading" />
      </>
    )
  }

  // Combine flags and loading state into a single context value
  const featureFlagsContextValue = {
    flags: featureFlags || defaultFeatureFlagsContext.flags,
    loaded: featureFlagsLoaded,
  }

  //we can use FeaturesContext and featuresContext.feature_name returns whether feature is enabled or not
  return (
    <AuthContext.Provider value={{ currentUser }}>
      <SystemFlagContext.Provider value={features.profile_dependency}>
        <FeatureFlagsContext.Provider value={featureFlagsContextValue}>
          <FeaturesContext.Provider value={features}>
            {children}
          </FeaturesContext.Provider>
        </FeatureFlagsContext.Provider>
      </SystemFlagContext.Provider>
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
